import { i18n } from '@/shared/i18n';

export interface PerformanceDailyMetric {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

export interface CurrencySpend {
  currency: string;
  spend: number;
  campaignCount: number;
}

export interface PerformanceBreakdownRow {
  key: string;
  label: string;
  /** Null when the row spans more than one currency. */
  currency: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  /** False when nothing in this row reports spend. Render an em dash, never 0. */
  spendReported: boolean;
  /**
   * The creative's rendered preview, at the creative grain only. Absent whenever the
   * platform exposes none - a search ad has no image, and several platforms report
   * delivery per creative without exposing the asset. A row without one is still a row.
   */
  previewUrl?: string | null;
}

export interface ProjectPerformance {
  metrics: PerformanceDailyMetric[];
  spendByCurrency: CurrencySpend[];
  unknownCurrencySpend: number;
  unknownCurrencyCampaignCount: number;
  isSingleCurrency: boolean;
  impressions: number;
  clicks: number;
  conversions: number;
  dimension: 'platform' | 'campaign' | 'creative' | 'placement';
  breakdown: PerformanceBreakdownRow[];
  campaignCount: number;
  supersededCampaignIds: string[];
  /**
   * Campaigns in scope with no row for the creative or placement dimension - the platform
   * does not break delivery down this way, distinct from reporting zero. Always empty for
   * the platform and campaign dimensions.
   */
  campaignsWithoutBreakdown: string[];
}

export type CampaignRole = 'buying' | 'ad_serving';

export type MediaChannel = 'search' | 'social' | 'display' | 'video' | 'programmatic';

// Getters, not values: a resolved label here would freeze English at import.
export const MEDIA_CHANNEL_LABELS: Record<MediaChannel, string> = {
  get search() {
    return i18n.t('projects:mediaCampaign.channel.search');
  },
  get social() {
    return i18n.t('projects:mediaCampaign.channel.social');
  },
  get display() {
    return i18n.t('projects:mediaCampaign.channel.display');
  },
  get video() {
    return i18n.t('projects:mediaCampaign.channel.video');
  },
  get programmatic() {
    return i18n.t('projects:mediaCampaign.channel.programmatic');
  },
};

/**
 * Whether the campaign is actually delivering, which the API computes on read rather than
 * stores. Distinct from the campaign's own status, which is only what someone asked for:
 * an enabled campaign whose budget is spent is on hold, not running.
 */
export type ServingStatus =
  | 'eligible'
  | 'budget_hold'
  | 'manual_hold'
  | 'zero_budget'
  | 'over_pacing'
  | 'pending'
  | 'ended'
  | 'paused'
  | 'removed';

export const SERVING_STATUS_LABELS: Record<ServingStatus, string> = {
  get eligible() {
    return i18n.t('projects:mediaCampaign.servingStatus.eligible');
  },
  get budget_hold() {
    return i18n.t('projects:mediaCampaign.servingStatus.budgetHold');
  },
  get manual_hold() {
    return i18n.t('projects:mediaCampaign.servingStatus.draft');
  },
  get zero_budget() {
    return i18n.t('projects:mediaCampaign.servingStatus.zeroBudget');
  },
  get over_pacing() {
    return i18n.t('projects:mediaCampaign.servingStatus.overPacing');
  },
  get pending() {
    return i18n.t('projects:mediaCampaign.servingStatus.pending');
  },
  get ended() {
    return i18n.t('projects:mediaCampaign.servingStatus.ended');
  },
  get paused() {
    return i18n.t('projects:mediaCampaign.servingStatus.paused');
  },
  get removed() {
    return i18n.t('projects:mediaCampaign.servingStatus.removed');
  },
};
